/**
 * Arm display-label rules for the experiment detail dashboards.
 *
 * Arms are named by their **domain letter**, matching `arm-combinations.ts` and
 * the create wizard: `A` = Control, `B` / `C` = the Lily-enriched variants.
 *
 *   2 arms → Control + Arm B
 *   3 arms → Control + Arm B + Arm C
 *
 * The analytics wire, however, names test arms by **slot index**:
 * `/performance-trend` returns arms `"A"`/`"B"` (CONTROL is the implicit
 * baseline) and `arm-comparison` returns `test_a`/`test_b` with `test_b: null`
 * on a 2-arm run. So the wire's first test slot (`testA`) is always domain Arm B,
 * and its second (`testB`) is always Arm C — an off-by-one that this module is
 * responsible for absorbing. Slot ids stay the data-addressing key everywhere
 * (chart series ids, table columns, CSV keys, toggle state); only the label is
 * translated through here.
 *
 * The mapping is independent of arm count — a slot's letter doesn't change when
 * a third arm is added. `armCount` is still needed to decide *whether* the
 * second test slot renders at all; see `hasSecondTestArm`.
 *
 * Caveat: `arm-combinations.ts` also supports `A_C` (Control vs C) and `B_C`
 * (no control), which this fixed mapping cannot express — `ExperimentDetail`
 * carries only `armCount`, no `arms`/`armCombination` field, so the detail page
 * can't tell an `A_B` run from an `A_C` one and assumes the former. If those
 * letters ever land on the wire, `resolveArmDisplaySlot` is the only function
 * that changes.
 */

import type { ArmId, ArmSlot } from '../types/experiment.types';

/** Which label a data slot is *rendered* as — the domain arm letter. */
export type ArmDisplaySlot = 'control' | 'armB' | 'armC';

/** Arm counts at or below this run a single test arm (the wire's `testA` slot). */
const SINGLE_TEST_ARM_MAX_COUNT = 2;

/** Wire slot → domain arm letter. Note the deliberate off-by-one (see file header). */
const DISPLAY_SLOT_BY_ARM_SLOT: Record<ArmSlot, ArmDisplaySlot> = {
  control: 'control',
  testA: 'armB',
  testB: 'armC',
};

/** `ArmId` (arm-comparison wire ids) → `ArmSlot` (chart/table slot ids). */
export function armIdToSlot(armId: ArmId): ArmSlot {
  if (armId === 'test_a') return 'testA';
  if (armId === 'test_b') return 'testB';
  return 'control';
}

/** Domain arm letter → data slot. `A` is Control; `B` / `C` are the variants. */
const SLOT_BY_ARM_LETTER: Record<string, ArmSlot> = {
  A: 'control',
  B: 'testA',
  C: 'testB',
};

/**
 * Resolves a domain arm letter to its data slot. Used for the wire fields that
 * identify arms by letter — `/performance-trend`'s `arms[].arm` and
 * `arm-comparison`'s `winner`. Case-insensitive; `null` for anything
 * unrecognised, so a payload from a newer backend is ignored rather than
 * guessed at.
 */
export function armSlotForLetter(letter: string | null | undefined): ArmSlot | null {
  if (letter == null) return null;
  return SLOT_BY_ARM_LETTER[letter.trim().toUpperCase()] ?? null;
}

/** `ArmSlot` → `ArmId`, the inverse of `armIdToSlot`. */
export function slotToArmId(slot: ArmSlot): ArmId {
  if (slot === 'testA') return 'test_a';
  if (slot === 'testB') return 'test_b';
  return 'control';
}

/** Maps a data slot to the arm letter it should be labelled as. */
export function resolveArmDisplaySlot(slot: ArmSlot): ArmDisplaySlot {
  return DISPLAY_SLOT_BY_ARM_SLOT[slot];
}

/** i18n key for an arm's label, e.g. `detail.arms.armB`. */
export function armLabelKey(slot: ArmSlot): string {
  return `detail.arms.${resolveArmDisplaySlot(slot)}`;
}

/** i18n key for an arm's parenthetical subtitle in the Arm Comparison header. */
export function armSubtitleKey(slot: ArmSlot): string {
  return `detail.arms.${resolveArmDisplaySlot(slot)}Subtitle`;
}

/**
 * i18n key for a **domain arm letter** sent by the wire, for the endpoints that
 * name arms directly (`/roas-comparison`'s `control_arm` / `test_arms`) rather
 * than by slot. Returns `null` for an unrecognised letter so the caller can show
 * the raw letter instead of mislabelling a newer backend's arm.
 *
 * Unlike the slot-based mapping this is position-independent, so it labels a
 * control-less run (`control_arm: "B"`) correctly — see the file header caveat.
 */
export function armLetterLabelKey(letter: string | null | undefined): string | null {
  const slot = armSlotForLetter(letter);
  return slot ? armLabelKey(slot) : null;
}

/** Plain-English labels for non-React consumers (chatbot narration builds raw strings). */
const ARM_LABEL_TEXT: Record<ArmDisplaySlot, string> = {
  control: 'Control',
  armB: 'Arm B',
  armC: 'Arm C',
};

/** Untranslated arm label — for the chatbot narration builders, which emit raw text. */
export function armLabelText(slot: ArmSlot): string {
  return ARM_LABEL_TEXT[resolveArmDisplaySlot(slot)];
}

/** Untranslated arm label keyed by the arm-comparison `ArmId` wire value. */
export function armIdLabelText(armId: ArmId): string {
  return armLabelText(armIdToSlot(armId));
}

/**
 * Whether this experiment runs a second test arm (Arm C). `armCount` is the
 * ground truth here — analytics `armsPresent` can lag on a freshly-launched
 * 3-arm run — so an unknown count optimistically assumes both arms rather than
 * hiding a column that has data.
 */
export function hasSecondTestArm(armCount: number | undefined): boolean {
  return armCount === undefined ? true : armCount > SINGLE_TEST_ARM_MAX_COUNT;
}
